import { notification } from 'antd';
import { get } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { applicationsActions } from '@store/applications/applications.slice';
import { consumersActions } from '@store/consumers/consumers.slice';

import { RouteEnum } from '@enum/route.enum';
import { useLazyGetAccessTokenQuery } from '@hook/application/application.hooks';
import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { useLazyQuery } from '@hook/common/use-lazy-query.hook';
import { usePrevious } from '@hook/common/use-previous.hook';
import { useLazyLoadLimitConsumersQuery } from '@hook/consumer/consumer.hooks';
import { useStoreDispatch, useStoreSelector } from '@hook/store/store.hooks';
import { ConsumerInterface } from '@interface/consumer/consumer.interface';
import { PaginationCursorResponseInterface, ResponseDataInterface } from '@interface/logs/response-data.interface';

const PAGINATION_PER_PAGE = 25;

export const useConsumers = () => {
  const dispatch = useStoreDispatch();
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const isCredentialsExist = useStoreSelector((state) => state.credentials.data.length > 0);
  const credentials = useStoreSelector((state) => state.credentials.data);
  const count = useStoreSelector((state) => Object.keys(state.consumers.data).length);
  const prevCount = usePrevious(count);
  const [action, { isLoading, data }] = useLazyLoadLimitConsumersQuery();
  const { hasToken, current } = useCurrentApplication();

  const [search, setSearch] = useState('');
  const isInitialMount = useRef(true);

  const mergeConsumersIntoStore = (result: ResponseDataInterface<ConsumerInterface[]>) => {
    result?.data?.forEach((consumer) => dispatch(consumersActions.setConsumer({ data: consumer })));
  };

  const { action: refreshToken } = useLazyQuery(
    useLazyGetAccessTokenQuery(),
    applicationsActions,
    applicationsActions.setAccess,
  );

  const handleApiCall = async (apiCall: () => Promise<any>) => {
    try {
      return await apiCall();
    } catch (err: any) {
      if (err?.status === 401) {
        // Refresh the application access token
        const apiKey = credentials[0]?.apiKey;
        if (apiKey && current.id) {
          await refreshToken({
            applicationId: current.id,
            apiKey,
          });
          // Retry the original call after token refresh
          return await apiCall();
        }
      }
      throw err;
    }
  };

  const onSelectConsumer = (consumerId: string) => () => {
    if (!isCredentialsExist) {
      return api.warning({
        message: 'Consumer need credentials',
        description:
          'Before accessing the consumer page, you need to create credentials. You can do this in the "Credentials" table by clicking on the "Add credentials" button',
      });
    }

    navigate(generatePath(RouteEnum.ConsumerView.path, { consumerId }));
  };

  const onNext = () => {
    void handleApiCall(() =>
      action({ pagination: { limit: PAGINATION_PER_PAGE, cursor: get(data, 'meta.next') }, ...(search && { search }) })
        .unwrap()
        .then(mergeConsumersIntoStore),
    );
  };

  const onPrevious = () => {
    void handleApiCall(() =>
      action({
        pagination: { limit: PAGINATION_PER_PAGE, cursor: get(data, 'meta.previous') },
        ...(search && { search }),
      })
        .unwrap()
        .then(mergeConsumersIntoStore),
    );
  };

  useEffect(() => {
    if (isCredentialsExist && hasToken && count !== prevCount) {
      void handleApiCall(() =>
        action({ pagination: { limit: PAGINATION_PER_PAGE } })
          .unwrap()
          .then(mergeConsumersIntoStore),
      );
    }
  }, [isCredentialsExist, hasToken, count, prevCount]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!isCredentialsExist || !hasToken) return;

    const timer = setTimeout(() => {
      void handleApiCall(() =>
        action({ pagination: { limit: PAGINATION_PER_PAGE }, ...(search && { search }) })
          .unwrap()
          .then(mergeConsumersIntoStore),
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  return {
    data: get(data, 'data', []),
    isLoading,
    pagination: get(data, 'meta') as unknown as PaginationCursorResponseInterface,
    onNext,
    onPrevious,
    onSelectConsumer,
    contextHolder,
    onSearch: setSearch,
  };
};
